import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/Seo";
import { StarRating } from "@/components/StarRating";
import { Slider } from "@/components/ui/slider";
import { Users, ArrowRight, SlidersHorizontal, X } from "lucide-react";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [type, setType] = useState("all");
  const [price, setPrice] = useState([0, 300]);
  const [sort, setSort] = useState("recommended");

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data);
      const prices = r.data.map((v) => v.price_per_day);
      if (prices.length) {
        const max = Math.ceil(Math.max(...prices) / 10) * 10;
        setPrice([0, max]);
      }
    }).catch(() => {});
  }, []);

  const priceMax = useMemo(() => {
    const prices = vehicles.map((v) => v.price_per_day);
    return prices.length ? Math.ceil(Math.max(...prices) / 10) * 10 : 300;
  }, [vehicles]);

  const types = ["all", ...Array.from(new Set(vehicles.map((v) => v.type)))];

  const filtered = useMemo(() => {
    let list = vehicles.filter((v) => {
      if (type !== "all" && v.type !== type) return false;
      if (v.price_per_day < price[0] || v.price_per_day > price[1]) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price_per_day - b.price_per_day);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price_per_day - a.price_per_day);
    else if (sort === "rating") list = [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return list;
  }, [vehicles, type, price, sort]);

  const resetFilters = () => {
    setType("all");
    setPrice([0, priceMax]);
    setSort("recommended");
  };

  const filtersActive = type !== "all" || price[0] !== 0 || price[1] !== priceMax || sort !== "recommended";

  return (
    <div className="container-bl py-10" data-testid="page-vehicles">
      <Seo
        title="Vehicle Rentals in Oxnard, Ventura County | B&L Rentals"
        description="Browse our full fleet of Oxnard rental vehicles — shuttle vans, electric SUVs, family minivans, and compact EVs. Book online in minutes."
        canonical={typeof window !== "undefined" ? window.location.origin + "/vehicles" : undefined}
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Vehicles" }]} />

      <div>
        <h1 className="text-4xl sm:text-5xl font-bold font-heading text-white">Vehicle rentals in Oxnard</h1>
        <p className="mt-3 text-[#AEB9C9] max-w-2xl">All rentals include <b>unlimited miles</b>, full tank / full charge at pickup, and no mileage restrictions anywhere in California.</p>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* FILTERS SIDEBAR */}
        <aside className="lg:sticky lg:top-24 h-fit" data-testid="filters-sidebar">
          <div className="card-bl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-semibold text-white">
                <SlidersHorizontal size={16} className="text-[#E2AB20]" /> Filters
              </div>
              {filtersActive && (
                <button onClick={resetFilters} className="text-xs text-[#E2AB20] hover:underline flex items-center gap-1" data-testid="filters-reset">
                  <X size={12} /> Reset
                </button>
              )}
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[#AEB9C9] font-semibold">Vehicle type</div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="vehicle-type-filter">
                {types.map((t) => (
                  <button
                    key={t}
                    data-testid={`filter-${t}`}
                    onClick={() => setType(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${type === t ? "bg-[#E2AB20] text-[#010F27] border-[#E2AB20]" : "bg-white/5 text-[#E5EAF2] border-white/20 hover:border-[#E2AB20] hover:text-white"}`}
                  >
                    {t === "all" ? "All types" : t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-[#AEB9C9] font-semibold">Price per day</div>
                <div className="text-xs font-medium text-white" data-testid="price-label">${price[0]} – ${price[1]}</div>
              </div>
              <div className="mt-4 px-1">
                <Slider
                  value={price}
                  onValueChange={setPrice}
                  min={0}
                  max={priceMax}
                  step={5}
                  minStepsBetweenThumbs={1}
                  data-testid="price-slider"
                />
                <div className="mt-2 flex items-center justify-between text-[10px] text-[#AEB9C9]">
                  <span>$0</span>
                  <span>${priceMax}</span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[#AEB9C9] font-semibold">Sort by</div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="sort-select"
                className="mt-3 w-full border border-white/20 rounded-md px-3 py-2 text-sm bg-white/5 text-white focus:outline-none focus:border-[#E2AB20] [&>option]:bg-[#010F27] [&>option]:text-white"
              >
                <option value="recommended">Recommended</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
                <option value="rating">Highest rated</option>
              </select>
            </div>
          </div>
        </aside>

        {/* RESULTS */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-[#AEB9C9]" data-testid="results-count">
              Showing <b>{filtered.length}</b> of {vehicles.length} vehicles
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="card-bl p-10 text-center" data-testid="no-results">
              <div className="text-[#E5EAF2] font-medium">No vehicles match your filters.</div>
              <button onClick={resetFilters} className="mt-3 link-blue text-sm">Reset filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((v) => (
                <Link
                  key={v.vehicle_id}
                  to={`/vehicles/${v.slug}`}
                  data-testid={`vehicle-list-${v.slug}`}
                  className="card-bl group transition-transform hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img src={v.hero_image} alt={`Rent ${v.name} in Oxnard`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">{v.type} · {v.year}</div>
                      <StarRating value={v.rating || 5} size={12} />
                    </div>
                    <h2 className="mt-2 font-heading font-semibold text-xl text-white">{v.name}</h2>
                    <p className="mt-1 text-sm text-[#AEB9C9] line-clamp-2">{v.short_desc}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-white"><span className="text-2xl font-bold">${v.price_per_day}</span><span className="text-sm text-[#AEB9C9]">/day</span></div>
                      <div className="text-xs text-[#AEB9C9] flex items-center gap-2">
                        <span className="flex items-center gap-1"><Users size={12} /> {v.capacity} seats</span>
                        <span className="text-[#E2AB20] flex items-center gap-1 font-medium">View <ArrowRight size={12} /></span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
